import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Landmark, Plus, Power, PowerOff, Sparkles, Wand2, CheckCircle2, AlertTriangle,
  Building2, ArrowLeftRight, Undo2
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const CLASSES_CONTA = ['ATIVO', 'PASSIVO', 'PATRIMONIO_LIQUIDO', 'RECEITA', 'CUSTO', 'DESPESA'];
const CLASSES_PATRIMONIAIS = ['ATIVO', 'PASSIVO', 'PATRIMONIO_LIQUIDO'];

const CLASSE_LABEL = {
  ATIVO: 'Ativo',
  PASSIVO: 'Passivo',
  PATRIMONIO_LIQUIDO: 'Patrimônio Líquido',
  RECEITA: 'Receita',
  CUSTO: 'Custo',
  DESPESA: 'Despesa',
};

const CLASSE_COR = {
  ATIVO: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  PASSIVO: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  PATRIMONIO_LIQUIDO: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  RECEITA: 'bg-green-500/20 text-green-300 border-green-500/30',
  CUSTO: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  DESPESA: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const formatBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PlanoContasContabil = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const [loading, setLoading] = useState(true);
  const [contas, setContas] = useState([]);

  // Nova conta
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ codigo: '', nome: '', classe: 'DESPESA', tipo_conta: 'ANALITICA', parent_id: '' });
  const [saving, setSaving] = useState(false);

  // Saldos iniciais
  const [saldosInfo, setSaldosInfo] = useState({ saldos: [], total_ativo: 0, total_passivo_pl: 0, diferenca: 0, balanceado: true });
  const [saldosLoading, setSaldosLoading] = useState(false);
  const [saldoForm, setSaldoForm] = useState({});

  // Mapeamento
  const [sugestoes, setSugestoes] = useState(null);
  const [sugestoesLoading, setSugestoesLoading] = useState(false);
  const [mapeamentoSelecionado, setMapeamentoSelecionado] = useState({});
  const [confirmando, setConfirmando] = useState(false);

  // Reconciliação de caixa (D7 — Company.saldo_inicial legado x saldos iniciais contábeis)
  const [reconciliacao, setReconciliacao] = useState(null);
  const [semantica, setSemantica] = useState({ regras: [], pendencias: [] });
  const [semanticForm, setSemanticForm] = useState({});

  // Contas Bancárias (FASE 4a)
  const [bancos, setBancos] = useState([]);
  const [bancosLoading, setBancosLoading] = useState(false);
  const [implantacao, setImplantacao] = useState(null);
  const [showBancoModal, setShowBancoModal] = useState(false);
  const [bancoForm, setBancoForm] = useState({ nome: '', banco: '', agencia: '', numero: '', titular: '', tipo: 'CORRENTE', data_abertura: '', saldo_data_corte: '', evidencia_saldo: '' });
  const [savingBanco, setSavingBanco] = useState(false);

  // Transferências entre Contas Bancárias (FASE 4a)
  const [transferencias, setTransferencias] = useState([]);
  const [transferenciasLoading, setTransferenciasLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ conta_bancaria_origem_id: '', conta_bancaria_destino_id: '', valor: '', data: '', descricao: '' });
  const [savingTransfer, setSavingTransfer] = useState(false);

  const fetchContas = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/contabil/plano-contas/${company.id}`);
      setContas(res.data.contas || []);
    } catch (error) {
      toast.error('Erro ao carregar plano de contas contábil');
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  const fetchSaldos = useCallback(async () => {
    if (!company?.id) return;
    setSaldosLoading(true);
    try {
      const res = await axiosInstance.get(`/contabil/saldos-iniciais/${company.id}`);
      setSaldosInfo(res.data);
    } catch (error) {
      toast.error('Erro ao carregar saldos iniciais');
    } finally {
      setSaldosLoading(false);
    }
  }, [company?.id]);

  const fetchReconciliacao = useCallback(async () => {
    if (!company?.id) return;
    try {
      const res = await axiosInstance.get(`/contabil/reconciliacao-caixa/${company.id}`);
      setReconciliacao(res.data);
    } catch (error) {
      // não bloqueia a tela — painel informativo
    }
  }, [company?.id]);

  const fetchBancos = useCallback(async () => {
    if (!company?.id) return;
    setBancosLoading(true);
    try {
      const res = await axiosInstance.get(`/contabil/contas-bancarias/${company.id}`);
      setBancos(res.data.contas_bancarias || []);
    } catch (error) {
      toast.error('Erro ao carregar contas bancárias');
    } finally {
      setBancosLoading(false);
    }
  }, [company?.id]);

  const fetchImplantacao = useCallback(async () => {
    if (!company?.id) return;
    const res = await axiosInstance.get(`/contabil/cobertura/implantacao/${company.id}/config`);
    setImplantacao(res.data.config ? {...res.data.config,...res.data.periodo} : null);
  }, [company?.id]);

  const fetchTransferencias = useCallback(async () => {
    if (!company?.id) return;
    setTransferenciasLoading(true);
    try {
      const res = await axiosInstance.get(`/contabil/transferencias/${company.id}`);
      setTransferencias(res.data.transferencias || []);
    } catch (error) {
      toast.error('Erro ao carregar transferências');
    } finally {
      setTransferenciasLoading(false);
    }
  }, [company?.id]);

  const fetchSemantica = useCallback(async () => {
    if (!company?.id) return;
    try {
      const [rules, pending] = await Promise.all([
        axiosInstance.get(`/contabil/semantica-dre/${company.id}`),
        axiosInstance.get(`/contabil/semantica-dre/${company.id}/pendencias`),
      ]);
      setSemantica({ regras: rules.data.regras || [], pendencias: pending.data.pendencias || [] });
    } catch (_) { /* painel diagnóstico não bloqueia o restante do plano */ }
  }, [company?.id]);

  useEffect(() => {
    fetchContas();
    fetchSaldos();
    fetchReconciliacao();
    fetchBancos();
    fetchImplantacao();
    fetchTransferencias();
    fetchSemantica();
  }, [fetchContas, fetchSaldos, fetchReconciliacao, fetchBancos, fetchTransferencias, fetchImplantacao, fetchSemantica]);

  const saveSemantic = async (accountId) => {
    const value = semanticForm[accountId] || {};
    if (!value.role || !value.valid_from) return toast.error('Escolha explicitamente o papel e a vigência');
    try {
      await axiosInstance.post(`/contabil/semantica-dre/${company.id}`, {
        account_id: accountId, role: value.role, presentation_group: value.presentation_group || value.role,
        presentation_order: Number(value.presentation_order || 100), display_sign: 1,
        valid_from: value.valid_from, valid_to: value.valid_to || null,
      });
      toast.success('Semântica DRE registrada com vigência'); fetchSemantica();
    } catch (error) { toast.error(error.response?.data?.detail || 'Semântica não registrada'); }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await axiosInstance.post(`/contabil/plano-contas/${company.id}/seed-defaults`);
      toast.success(res.data.message);
      fetchContas();
    } catch (error) {
      toast.error('Erro ao gerar plano de contas padrão');
    }
  };

  const handleToggleAtivo = async (conta) => {
    try {
      await axiosInstance.patch(`/contabil/plano-contas/${conta.id}/toggle?ativo=${!conta.ativo}`);
      toast.success(conta.ativo ? 'Conta desativada' : 'Conta ativada');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao alterar status da conta');
    }
  };

  const contasSinteticas = contas.filter((c) => c.tipo_conta === 'SINTETICA');

  const handleCreate = () => {
    setFormData({ codigo: '', nome: '', classe: 'DESPESA', tipo_conta: 'ANALITICA', parent_id: '' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.post('/contabil/plano-contas', {
        company_id: company.id,
        codigo: formData.codigo,
        nome: formData.nome,
        classe: formData.classe,
        tipo_conta: formData.tipo_conta,
        parent_id: formData.parent_id || null,
      });
      toast.success('Conta contábil criada');
      setShowModal(false);
      fetchContas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta contábil');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Saldos Iniciais ----------

  const contasPatrimoniaisAnaliticas = contas.filter(
    (c) => CLASSES_PATRIMONIAIS.includes(c.classe) && c.tipo_conta === 'ANALITICA' && c.ativo
  );

  const saldoPorConta = Object.fromEntries((saldosInfo.saldos || []).map((s) => [s.conta_contabil_id, s]));

  const handleSalvarSaldo = async (contaId) => {
    const valor = parseFloat(saldoForm[contaId]?.valor);
    const data_referencia = implantacao?.data_base_abertura;
    const evidencia = saldoForm[contaId]?.evidencia;
    if (Number.isNaN(valor) || !data_referencia || !evidencia) {
      toast.error('Informe valor, data e evidência');
      return;
    }
    try {
      await axiosInstance.post(`/contabil/saldos-iniciais/${company.id}`, {
        company_id: company.id,
        conta_contabil_id: contaId,
        valor,
        data_referencia,
        evidencia,
      });
      toast.success('Saldo inicial salvo');
      fetchSaldos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar saldo inicial');
    }
  };

  const handleAprovarAbertura = async () => {
    try { await axiosInstance.post(`/contabil/saldos-iniciais/${company.id}/aprovar`); toast.success('Balanço de abertura aprovado'); fetchSaldos(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Abertura não pode ser aprovada'); }
  };

  const handleRemoverSaldo = async (saldoId) => {
    try {
      await axiosInstance.delete(`/contabil/saldos-iniciais/${saldoId}`);
      toast.success('Saldo inicial removido');
      fetchSaldos();
    } catch (error) {
      toast.error('Erro ao remover saldo inicial');
    }
  };

  // ---------- Mapeamento de Categorias ----------

  const handleSugerirMapeamento = async () => {
    setSugestoesLoading(true);
    try {
      const res = await axiosInstance.get(`/contabil/mapeamento-categorias/${company.id}/sugestao`);
      setSugestoes(res.data.sugestoes || []);
      const inicial = {};
      (res.data.sugestoes || []).forEach((s) => {
        if (s.conta_contabil_sugerida_id) inicial[s.expense_category_id] = s.conta_contabil_sugerida_id;
      });
      setMapeamentoSelecionado(inicial);
    } catch (error) {
      toast.error('Erro ao gerar sugestão de mapeamento');
    } finally {
      setSugestoesLoading(false);
    }
  };

  const handleConfirmarMapeamento = async () => {
    const itens = Object.entries(mapeamentoSelecionado)
      .filter(([, contaId]) => !!contaId)
      .map(([expense_category_id, conta_contabil_id]) => ({ expense_category_id, conta_contabil_id }));

    if (itens.length === 0) {
      toast.error('Selecione ao menos um mapeamento para confirmar');
      return;
    }

    setConfirmando(true);
    try {
      const res = await axiosInstance.post(`/contabil/mapeamento-categorias/${company.id}/confirmar`, { itens });
      toast.success(res.data.message);
      handleSugerirMapeamento();
    } catch (error) {
      toast.error('Erro ao confirmar mapeamento');
    } finally {
      setConfirmando(false);
    }
  };

  const contasAnaliticasAtivas = contas.filter((c) => c.tipo_conta === 'ANALITICA' && c.ativo);

  // ---------- Contas Bancárias ----------

  const handleCreateBanco = () => {
    if (!implantacao?.data_base_abertura) return toast.error('Defina primeiro o MÊS-BASE CONTÁBIL na Implantação');
    setBancoForm({ nome: '', banco: '', agencia: '', numero: '', titular: '', tipo: 'CORRENTE', data_abertura: implantacao.data_base_abertura, saldo_data_corte: '', evidencia_saldo: '' });
    setShowBancoModal(true);
  };

  const handleStatusBanco = async (banco,status) => {
    try { await axiosInstance.post(`/contabil/contas-bancarias/${banco.id}/status?status=${status}`); toast.success(`Banco marcado como ${status}`); fetchBancos(); }
    catch(error){ toast.error(error.response?.data?.detail || 'Status bancário não alterado'); }
  };

  const handleSalvarBanco = async (e) => {
    e.preventDefault();
    setSavingBanco(true);
    try {
      await axiosInstance.post('/contabil/contas-bancarias', { company_id: company.id, ...bancoForm });
      toast.success('Conta bancária criada');
      setShowBancoModal(false);
      fetchBancos();
      fetchContas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta bancária');
    } finally {
      setSavingBanco(false);
    }
  };

  const handleToggleBanco = async (banco) => {
    try {
      await axiosInstance.patch(`/contabil/contas-bancarias/${banco.id}/toggle?ativo=${!banco.ativo}`);
      toast.success(banco.ativo ? 'Conta bancária desativada' : 'Conta bancária ativada');
      fetchBancos();
    } catch (error) {
      toast.error('Erro ao alterar status da conta bancária');
    }
  };

  // ---------- Transferências ----------

  const bancosAtivos = bancos.filter((b) => b.ativo);

  const handleCreateTransferencia = () => {
    setTransferForm({ conta_bancaria_origem_id: '', conta_bancaria_destino_id: '', valor: '', data: new Date().toISOString().slice(0, 10), descricao: '' });
    setShowTransferModal(true);
  };

  const handleSalvarTransferencia = async (e) => {
    e.preventDefault();
    if (transferForm.conta_bancaria_origem_id === transferForm.conta_bancaria_destino_id) {
      toast.error('Conta de origem e destino não podem ser a mesma');
      return;
    }
    setSavingTransfer(true);
    try {
      await axiosInstance.post('/contabil/transferencias', {
        company_id: company.id,
        conta_bancaria_origem_id: transferForm.conta_bancaria_origem_id,
        conta_bancaria_destino_id: transferForm.conta_bancaria_destino_id,
        valor: parseFloat(transferForm.valor),
        data: transferForm.data,
        descricao: transferForm.descricao || null,
      });
      toast.success('Transferência efetivada');
      setShowTransferModal(false);
      fetchTransferencias();
      fetchBancos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao efetivar transferência');
    } finally {
      setSavingTransfer(false);
    }
  };

  const handleEstornarTransferencia = async (transferenciaId) => {
    if (!window.confirm('Estornar esta transferência? O saldo entre as duas contas será revertido.')) return;
    try {
      await axiosInstance.delete(`/contabil/transferencias/${transferenciaId}`);
      toast.success('Transferência estornada');
      fetchTransferencias();
      fetchBancos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar transferência');
    }
  };

  const nomeBanco = (id) => bancos.find((b) => b.id === id)?.nome || '—';

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="contabil-plano-contas" />

      <div className="flex-1 p-3 pt-14 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <SubscriptionCard user={user} />

          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <Landmark className="w-8 h-8 text-cyan-400" />
              Plano de Contas Contábil
            </h1>
            <p className="text-zinc-400 mt-1">
              Estrutura contábil hierárquica (Ativo/Passivo/Patrimônio Líquido/Receita/Custo/Despesa) — independente das
              Categorias Financeiras usadas nos lançamentos hoje. Base para o razão, balancete e balanço patrimonial.
            </p>
          </div>

          <Tabs defaultValue="contas" className="w-full">
            <TabsList className="glass border-white/10 mb-6 flex-wrap">
              <TabsTrigger value="contas">Contas</TabsTrigger>
              <TabsTrigger value="saldos">Saldos Iniciais</TabsTrigger>
              <TabsTrigger value="bancos">Contas Bancárias</TabsTrigger>
              <TabsTrigger value="transferencias">Transferências</TabsTrigger>
              <TabsTrigger value="mapeamento">Mapear Categorias</TabsTrigger>
              <TabsTrigger value="semantica">Semântica DRE</TabsTrigger>
            </TabsList>

            {/* ---------- ABA 1: CONTAS ---------- */}
            <TabsContent value="contas" className="space-y-4">
              <div className="flex items-center justify-end gap-2">
                {contas.length === 0 && (
                  <Button
                    onClick={handleSeedDefaults}
                    variant="outline"
                    className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Gerar Plano de Contas Padrão
                  </Button>
                )}
                <Button onClick={handleCreate} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Conta
                </Button>
              </div>

              {contas.length === 0 && !loading && (
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-6 pb-6">
                    <p className="text-sm text-zinc-300">
                      Nenhuma conta contábil cadastrada. Clique em "Gerar Plano de Contas Padrão" para criar a estrutura
                      inicial (Ativo, Passivo, Patrimônio Líquido, Receitas, Custos e Despesas).
                    </p>
                  </CardContent>
                </Card>
              )}

              {loading ? (
                <div className="text-center text-zinc-400 py-8">Carregando...</div>
              ) : (
                <div className="space-y-2">
                  {contas.map((conta) => (
                    <div
                      key={conta.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        conta.ativo ? 'bg-zinc-900 border-zinc-800' : 'bg-zinc-900/50 border-zinc-800 opacity-50'
                      }`}
                      style={{ marginLeft: `${(conta.nivel - 1) * 20}px` }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-zinc-500 w-16">{conta.codigo}</span>
                        <span className={conta.tipo_conta === 'SINTETICA' ? 'font-semibold' : ''}>{conta.nome}</span>
                        <Badge className={CLASSE_COR[conta.classe]}>{CLASSE_LABEL[conta.classe]}</Badge>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                          {conta.tipo_conta === 'SINTETICA' ? 'Sintética' : 'Analítica'}
                        </Badge>
                        <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                          {conta.natureza === 'DEVEDORA' ? 'Devedora' : 'Credora'}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleAtivo(conta)}
                        className={`h-8 w-8 p-0 ${conta.ativo ? 'hover:bg-orange-500/20 hover:text-orange-400' : 'hover:bg-green-500/20 hover:text-green-400'}`}
                      >
                        {conta.ativo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ---------- ABA 2: SALDOS INICIAIS ---------- */}
            <TabsContent value="saldos" className="space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Saldo de abertura por conta</CardTitle>
                  <CardDescription>
                    Para empresas que já têm patrimônio, obrigações e saldos antes de começar a usar o sistema.
                    Só contas patrimoniais (Ativo/Passivo/Patrimônio Líquido) analíticas aceitam saldo inicial.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {contasPatrimoniaisAnaliticas.length === 0 && (
                    <p className="text-sm text-zinc-500">Gere o plano de contas padrão na aba "Contas" primeiro.</p>
                  )}
                  {contasPatrimoniaisAnaliticas.map((conta) => {
                    const saldoExistente = saldoPorConta[conta.id];
                    return (
                      <div key={conta.id} className="grid grid-cols-1 md:grid-cols-[1fr_130px_140px_180px_auto] gap-2 items-end p-2 border-b border-zinc-800">
                        <div>
                          <Label className="text-xs text-zinc-500">{conta.codigo}</Label>
                          <p className="text-sm">{conta.nome} <Badge className={`${CLASSE_COR[conta.classe]} ml-1`}>{CLASSE_LABEL[conta.classe]}</Badge></p>
                        </div>
                        <div><Label className="text-xs text-zinc-500">Evidência/origem</Label><Input defaultValue={saldoExistente?.evidencia} className="bg-zinc-800 border-zinc-700" onChange={(e) => setSaldoForm({ ...saldoForm, [conta.id]: { ...saldoForm[conta.id], evidencia: e.target.value } })}/></div>
                        <div>
                          <Label className="text-xs text-zinc-500">Valor</Label>
                          <Input
                            type="number"
                            step="0.01"
                            defaultValue={saldoExistente?.valor}
                            className="bg-zinc-800 border-zinc-700"
                            onChange={(e) => setSaldoForm({ ...saldoForm, [conta.id]: { ...saldoForm[conta.id], valor: e.target.value } })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">Data referência</Label>
                          <Input
                            type="date"
                            value={implantacao?.data_base_abertura || saldoExistente?.data_referencia || ''}
                            disabled
                            className="bg-zinc-800 border-zinc-700"
                            onChange={() => {}}
                          />
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => handleSalvarSaldo(conta.id)}>Salvar</Button>
                          {saldoExistente && (
                            <Button size="sm" variant="ghost" className="hover:bg-red-500/20 hover:text-red-400" onClick={() => handleRemoverSaldo(saldoExistente.id)}>
                              Remover
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className={saldosInfo.balanceado ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}>
                <CardContent className="pt-6 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    {saldosInfo.balanceado ? (
                      <CheckCircle2 className="w-6 h-6 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    )}
                    <div>
                      <p className="font-medium">{saldosLoading ? 'Calculando...' : (saldosInfo.balanceado ? 'Balanço de abertura batendo' : 'Balanço de abertura desbalanceado')}</p>
                      <p className="text-sm text-zinc-400">Total Devedor (Ativo) precisa ser igual a Total Credor (Passivo + Patrimônio Líquido)</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div><span className="text-zinc-500">Ativo:</span> <span className="font-mono">{formatBRL(saldosInfo.total_ativo)}</span></div>
                    <div><span className="text-zinc-500">Passivo:</span> <span className="font-mono">{formatBRL(saldosInfo.total_passivo)}</span></div>
                    <div><span className="text-zinc-500">PL:</span> <span className="font-mono">{formatBRL(saldosInfo.total_pl)}</span></div>
                    <div><span className="text-zinc-500">Passivo+PL:</span> <span className="font-mono">{formatBRL(saldosInfo.total_passivo_pl)}</span></div>
                    <div><span className="text-zinc-500">Diferença:</span> <span className="font-mono">{formatBRL(saldosInfo.diferenca)}</span></div>
                  </div>
                  <Button disabled={!saldosInfo.balanceado || !(saldosInfo.saldos || []).length} onClick={handleAprovarAbertura}>Aprovar abertura</Button>
                </CardContent>
              </Card>

              {reconciliacao && (
                <Card className={reconciliacao.reconciliado ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'}>
                  <CardHeader>
                    <CardTitle className="text-base">Reconciliação de Caixa (saldo legado × saldos iniciais contábeis)</CardTitle>
                    <CardDescription>
                      Compara o saldo inicial legado (usado hoje pelo DFC/Dashboard/App do Proprietário) com a soma dos
                      saldos iniciais das contas de Caixa e Bancos acima. Só informativo — nenhum ajuste automático é feito.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {reconciliacao.reconciliado ? (
                        <CheckCircle2 className="w-6 h-6 text-green-400" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-amber-400" />
                      )}
                      <p className="text-sm text-zinc-300">
                        {reconciliacao.saldo_legado_definido
                          ? (reconciliacao.reconciliado ? 'Os dois saldos batem' : 'Os dois saldos divergem')
                          : 'Nenhum saldo inicial legado definido para esta empresa'}
                      </p>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div><span className="text-zinc-500">Saldo legado:</span> <span className="font-mono">{formatBRL(reconciliacao.saldo_legado)}</span></div>
                      <div><span className="text-zinc-500">Soma Caixa/Bancos:</span> <span className="font-mono">{formatBRL(reconciliacao.soma_saldos_iniciais_caixa_bancos)}</span></div>
                      <div><span className="text-zinc-500">Diferença:</span> <span className="font-mono">{formatBRL(reconciliacao.diferenca)}</span></div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ---------- ABA BANCOS: CONTAS BANCÁRIAS ---------- */}
            <TabsContent value="bancos" className="space-y-4">
              <div className="flex items-center justify-end">
                <Button onClick={handleCreateBanco} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Conta Bancária
                </Button>
              </div>

              {bancos.length === 0 && !bancosLoading && (
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-6 pb-6">
                    <p className="text-sm text-zinc-300">
                      Nenhuma conta bancária cadastrada. Sem uma conta bancária selecionada, pagamentos e recebimentos
                      continuam usando a conta genérica "Caixa e Bancos" (1.1.01) — comportamento de hoje, sem mudança.
                    </p>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                {bancos.map((banco) => (
                  <div
                    key={banco.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      banco.ativo ? 'bg-zinc-900 border-zinc-800' : 'bg-zinc-900/50 border-zinc-800 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="font-medium">{banco.nome}</p>
                        <p className="text-xs text-zinc-500">
                          {[banco.banco, banco.agencia && `Ag. ${banco.agencia}`, banco.numero, banco.titular && `Titular: ${banco.titular}`].filter(Boolean).join(' · ') || 'Sem detalhes cadastrados'}
                        </p>
                      </div>
                      <Badge variant="outline" className="border-zinc-700 text-zinc-400">{banco.tipo}</Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs"><p className="font-mono text-sm">{formatBRL(banco.saldo)}</p><p>{banco.status || 'RASCUNHO'} · {banco.evidencia_status}</p><p className="text-cyan-300">Conta contábil: {banco.conta_contabil?.codigo} · {banco.conta_contabil?.nome}</p></div>
                      {banco.status==='RASCUNHO'&&<Button size="sm" variant="outline" onClick={()=>handleStatusBanco(banco,'REVISADO')}>Revisar</Button>}
                      {banco.status==='REVISADO'&&<Button size="sm" onClick={()=>handleStatusBanco(banco,'APROVADO')}>Aprovar</Button>}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleBanco(banco)}
                        className={`h-8 w-8 p-0 ${banco.ativo ? 'hover:bg-orange-500/20 hover:text-orange-400' : 'hover:bg-green-500/20 hover:text-green-400'}`}
                      >
                        {banco.ativo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ---------- ABA TRANSFERÊNCIAS ---------- */}
            <TabsContent value="transferencias" className="space-y-4">
              <div className="flex items-center justify-end">
                <Button
                  onClick={handleCreateTransferencia}
                  disabled={bancosAtivos.length < 2}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                >
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Nova Transferência
                </Button>
              </div>

              {bancosAtivos.length < 2 && (
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="pt-6 pb-6">
                    <p className="text-sm text-zinc-300">Cadastre ao menos 2 contas bancárias ativas para transferir entre elas.</p>
                  </CardContent>
                </Card>
              )}

              {transferencias.length === 0 && !transferenciasLoading && bancosAtivos.length >= 2 && (
                <p className="text-sm text-zinc-500">Nenhuma transferência registrada.</p>
              )}

              <div className="space-y-2">
                {transferencias.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border bg-zinc-900 border-zinc-800">
                    <div className="flex items-center gap-3">
                      <ArrowLeftRight className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-sm">{nomeBanco(t.conta_bancaria_origem_id)} → {nomeBanco(t.conta_bancaria_destino_id)}</p>
                        <p className="text-xs text-zinc-500">{t.data} {t.descricao ? `· ${t.descricao}` : ''}</p>
                      </div>
                      <Badge className={t.status === 'ESTORNADA' ? 'bg-zinc-700 text-zinc-300' : 'bg-green-500/20 text-green-300 border-green-500/30'}>
                        {t.status === 'ESTORNADA' ? 'Estornada' : 'Efetivada'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm">{formatBRL(t.valor)}</span>
                      {t.status !== 'ESTORNADA' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEstornarTransferencia(t.id)}
                          className="h-8 w-8 p-0 hover:bg-red-500/20 hover:text-red-400"
                          title="Estornar"
                        >
                          <Undo2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ---------- ABA 3: MAPEAR CATEGORIAS ---------- */}
            <TabsContent value="mapeamento" className="space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Mapear Categorias Financeiras → Conta Contábil</CardTitle>
                  <CardDescription>
                    Só uma ponte de transição, revisável a qualquer momento. Nada é gravado até você confirmar cada linha —
                    as sugestões podem errar (ex.: uma categoria "Veículos" pode misturar aquisição e manutenção).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={handleSugerirMapeamento} disabled={sugestoesLoading} variant="outline" className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20">
                    <Wand2 className="w-4 h-4 mr-2" />
                    {sugestoesLoading ? 'Gerando sugestão...' : 'Sugerir Mapeamento'}
                  </Button>

                  {sugestoes && sugestoes.length === 0 && (
                    <p className="text-sm text-zinc-500">Todas as categorias ativas já estão mapeadas.</p>
                  )}

                  {sugestoes && sugestoes.length > 0 && (
                    <div className="space-y-2">
                      {sugestoes.map((s) => (
                        <div key={s.expense_category_id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center p-2 border-b border-zinc-800">
                          <div>
                            <p className="text-sm">{s.expense_category_name}</p>
                            {s.confianca && (
                              <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs mt-1">
                                sugestão: confiança {s.confianca}
                              </Badge>
                            )}
                          </div>
                          <Select
                            value={mapeamentoSelecionado[s.expense_category_id] || ''}
                            onValueChange={(value) => setMapeamentoSelecionado({ ...mapeamentoSelecionado, [s.expense_category_id]: value })}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue placeholder="Selecionar conta contábil..." />
                            </SelectTrigger>
                            <SelectContent>
                              {contasAnaliticasAtivas.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.codigo} — {c.nome}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                      <Button
                        onClick={handleConfirmarMapeamento}
                        disabled={confirmando}
                        className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 mt-2"
                      >
                        {confirmando ? 'Confirmando...' : 'Confirmar Mapeamento'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="semantica" className="space-y-4">
              <Card className="glass border-white/10"><CardHeader><CardTitle>Semântica DRE por conta</CardTitle>
                <CardDescription>Nenhuma sugestão é aplicada automaticamente. Conta de resultado movimentada sem regra vigente bloqueia a DRE Ledger.</CardDescription>
              </CardHeader><CardContent className="space-y-3">
                {semantica.pendencias.length === 0 && <p className="text-green-300">Todas as contas analíticas de resultado possuem uma regra vigente única.</p>}
                {semantica.pendencias.map((item) => <div key={item.account_id} className="rounded border border-amber-500/30 p-3 space-y-2">
                  <div><b>{item.code} — {item.name}</b> <Badge variant="outline">{item.reason}</Badge></div>
                  <div className="grid md:grid-cols-4 gap-2">
                    <Select value={semanticForm[item.account_id]?.role || ''} onValueChange={(role)=>setSemanticForm({...semanticForm,[item.account_id]:{...(semanticForm[item.account_id]||{}),role}})}>
                      <SelectTrigger><SelectValue placeholder="Escolha o papel explicitamente" /></SelectTrigger><SelectContent>
                        {['RECEITA_BRUTA','DEDUCAO_RECEITA','CUSTO','DESPESA_ADMINISTRATIVA','DESPESA_COMERCIAL','RESULTADO_FINANCEIRO','OUTRA_RECEITA','OUTRA_DESPESA'].map(role=><SelectItem key={role} value={role}>{role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Grupo de apresentação" value={semanticForm[item.account_id]?.presentation_group || ''} onChange={e=>setSemanticForm({...semanticForm,[item.account_id]:{...(semanticForm[item.account_id]||{}),presentation_group:e.target.value}})} />
                    <Input type="date" value={semanticForm[item.account_id]?.valid_from || ''} onChange={e=>setSemanticForm({...semanticForm,[item.account_id]:{...(semanticForm[item.account_id]||{}),valid_from:e.target.value}})} />
                    <Button onClick={()=>saveSemantic(item.account_id)}>Confirmar regra</Button>
                  </div>
                </div>)}
                <p className="text-sm text-muted-foreground">Regras configuradas: {semantica.regras.length}. Alterações futuras devem encerrar a vigência anterior; o histórico não é reclassificado silenciosamente.</p>
              </CardContent></Card>
            </TabsContent>
          </Tabs>

          {/* Modal Nova Conta */}
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle>Nova Conta Contábil</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <Label htmlFor="codigo">Código *</Label>
                  <Input
                    id="codigo"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    placeholder="Ex: 6.1.05"
                    required
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label>Classe *</Label>
                  <Select value={formData.classe} onValueChange={(value) => setFormData({ ...formData, classe: value, parent_id: '' })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CLASSES_CONTA.map((c) => (
                        <SelectItem key={c} value={c}>{CLASSE_LABEL[c]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo *</Label>
                  <Select value={formData.tipo_conta} onValueChange={(value) => setFormData({ ...formData, tipo_conta: value })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SINTETICA">Sintética (agrupadora, não recebe lançamento)</SelectItem>
                      <SelectItem value="ANALITICA">Analítica (pode receber lançamento)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conta-pai (opcional)</Label>
                  <Select value={formData.parent_id} onValueChange={(value) => setFormData({ ...formData, parent_id: value })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                    <SelectContent>
                      {contasSinteticas.filter((c) => c.classe === formData.classe).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.codigo} — {c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">Só contas sintéticas da mesma classe aparecem aqui.</p>
                </div>
                <DialogFooter className="gap-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                    {saving ? 'Salvando...' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Modal Nova Conta Bancária */}
          <Dialog open={showBancoModal} onOpenChange={setShowBancoModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle>Nova Conta Bancária</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSalvarBanco} className="space-y-4">
                <div>
                  <Label htmlFor="banco-nome">Nome/Apelido *</Label>
                  <Input
                    id="banco-nome"
                    value={bancoForm.nome}
                    onChange={(e) => setBancoForm({ ...bancoForm, nome: e.target.value })}
                    placeholder="Ex: Conta Corrente Nubank"
                    required
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label htmlFor="banco-banco">Banco/instituição *</Label>
                  <Input
                    id="banco-banco"
                    value={bancoForm.banco}
                    onChange={(e) => setBancoForm({ ...bancoForm, banco: e.target.value })}
                    required
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="banco-agencia">Agência</Label>
                    <Input
                      id="banco-agencia"
                      value={bancoForm.agencia}
                      onChange={(e) => setBancoForm({ ...bancoForm, agencia: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="banco-numero">Número *</Label>
                    <Input
                      id="banco-numero"
                      value={bancoForm.numero}
                      onChange={(e) => setBancoForm({ ...bancoForm, numero: e.target.value })}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div><Label>Titular da conta *</Label><Input value={bancoForm.titular} onChange={e=>setBancoForm({...bancoForm,titular:e.target.value})} required className="bg-zinc-800 border-zinc-700"/></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={bancoForm.tipo} onValueChange={(value) => setBancoForm({ ...bancoForm, tipo: value })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CORRENTE">Conta Corrente</SelectItem>
                      <SelectItem value="POUPANCA">Poupança</SelectItem>
                      <SelectItem value="CAIXA">Caixa (dinheiro)</SelectItem>
                      <SelectItem value="DIGITAL">Conta Digital</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3"><div><Label>Data-base de abertura *</Label><Input type="date" value={bancoForm.data_abertura} disabled required className="bg-zinc-800 border-zinc-700"/></div><div><Label>Saldo ao fim do mês-base *</Label><Input type="number" step="0.01" value={bancoForm.saldo_data_corte} onChange={e=>setBancoForm({...bancoForm,saldo_data_corte:e.target.value===''?'':Number(e.target.value)})} required className="bg-zinc-800 border-zinc-700"/></div></div>
                <div><Label>Evidência/origem do saldo</Label><Input value={bancoForm.evidencia_saldo} onChange={e=>setBancoForm({...bancoForm,evidencia_saldo:e.target.value})} placeholder="Extrato, balanço assinado ou documento" className="bg-zinc-800 border-zinc-700"/><Badge className="mt-2">{bancoForm.evidencia_saldo?'COMPROVADO POR EVIDÊNCIA':'INFORMADO PELO USUÁRIO'}</Badge></div>
                <DialogFooter className="gap-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => setShowBancoModal(false)} className="border-zinc-700">Cancelar</Button>
                  <Button type="submit" disabled={savingBanco} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                    {savingBanco ? 'Salvando...' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Modal Nova Transferência */}
          <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle>Nova Transferência entre Contas</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSalvarTransferencia} className="space-y-4">
                <div>
                  <Label>Conta de origem *</Label>
                  <Select
                    value={transferForm.conta_bancaria_origem_id}
                    onValueChange={(value) => setTransferForm({ ...transferForm, conta_bancaria_origem_id: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      {bancosAtivos.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conta de destino *</Label>
                  <Select
                    value={transferForm.conta_bancaria_destino_id}
                    onValueChange={(value) => setTransferForm({ ...transferForm, conta_bancaria_destino_id: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      {bancosAtivos.filter((b) => b.id !== transferForm.conta_bancaria_origem_id).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="transfer-valor">Valor *</Label>
                    <Input
                      id="transfer-valor"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={transferForm.valor}
                      onChange={(e) => setTransferForm({ ...transferForm, valor: e.target.value })}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="transfer-data">Data *</Label>
                    <Input
                      id="transfer-data"
                      type="date"
                      value={transferForm.data}
                      onChange={(e) => setTransferForm({ ...transferForm, data: e.target.value })}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="transfer-descricao">Descrição (opcional)</Label>
                  <Input
                    id="transfer-descricao"
                    value={transferForm.descricao}
                    onChange={(e) => setTransferForm({ ...transferForm, descricao: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <p className="text-xs text-zinc-500">Transferência não gera receita nem despesa — é movimentação neutra entre duas contas patrimoniais.</p>
                <DialogFooter className="gap-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="border-zinc-700">Cancelar</Button>
                  <Button type="submit" disabled={savingTransfer} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                    {savingTransfer ? 'Efetivando...' : 'Efetivar Transferência'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default PlanoContasContabil;
